import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography, useMediaQuery, Box } from '@mui/material';
import { useGetMenuMaster } from 'api/menu';

// project-imports
import Dot from 'components/@extended/Dot';
import useConfig from 'hooks/useConfig';
import { dispatch } from 'store';
import { activeItem, openDrawer } from 'store/reducers/menu';

// types
import { MenuOrientation, ThemeMode } from 'types/config';
import { LinkTarget, NavItemType } from 'types/menu';

// permission imports
import { useHasPermission, useHasAnyPermission, useHasAllPermissions } from 'contexts/PermissionContext';

// ==============================|| NAVIGATION - ITEM ||============================== //

interface Props {
  item: NavItemType;
  level: number;
}

/**
 * A nav label that stays on one line and reveals its full name on hover.
 *
 * Two reasons a tooltip appears, and they are different things:
 *
 *  - the item declares a `titleTooltip`, meaning its label is a deliberate abbreviation
 *    ("Updates" for "Update & Training") and the full name lives only in the tooltip;
 *  - or the label has no abbreviation but has run out of room and been ellipsised, in
 *    which case the tooltip just recovers what the drawer cut off.
 *
 * With neither, no tooltip is attached at all. Attaching one unconditionally would make
 * short items like "Users" pop up a bubble repeating what is already on screen — noise on
 * every item to serve the one that needs it.
 *
 * Clipping is measured off the rendered element rather than guessed from character count:
 * the font is not monospaced and the space available depends on the drawer. Re-measured
 * when the label or the drawer state changes, which is when the answer can differ.
 */
const NavItemTitle = ({
  title,
  fullTitle,
  drawerOpen,
  sx
}: {
  title: string;
  fullTitle?: string;
  drawerOpen: boolean;
  sx: SxProps<Theme>;
}) => {
  const ref = useRef<HTMLElement | null>(null);
  const [clipped, setClipped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setClipped(el.scrollWidth > el.clientWidth);
  }, [title, drawerOpen]);

  // An explicit full name always wins; otherwise the tooltip only exists to undo an
  // ellipsis, so it repeats the label.
  const tooltip = fullTitle && fullTitle !== title ? fullTitle : title;
  const showTooltip = Boolean(fullTitle && fullTitle !== title) || clipped;

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      disableHoverListener={!showTooltip}
      disableFocusListener={!showTooltip}
      disableTouchListener={!showTooltip}
    >
      <Typography ref={ref} variant="h6" noWrap sx={sx}>
        {title}
      </Typography>
    </Tooltip>
  );
};

const NavItem = ({ item, level }: Props) => {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down('lg'));

  // Always call hooks - move permission checks to top
  const { menuOrientation } = useConfig();
  const { menuMaster } = useGetMenuMaster();
  const { pathname } = useLocation();

  // Permission hooks - always call them
  const singlePermissionCheck = useHasPermission(item.permission);
  const multiplePermissionCheck = useHasAnyPermission(item.permissions || []);
  const allPermissionsCheck = useHasAllPermissions(item.permissions || []);
  
  // Calculate permission access
  const hasAccess = (() => {
    if (item.permission) {
      return singlePermissionCheck;
    }
    if (item.permissions && item.permissions.length > 0) {
      return item.requireAll ? allPermissionsCheck : multiplePermissionCheck;
    }
    return true; // No permission requirements
  })();

  const drawerOpen = menuMaster?.isDashboardDrawerOpened ?? false;
  let itemTarget: LinkTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  // Only one menu item should be selected at a time
  // If pathname matches this item's url, it is selected; otherwise, not
  const isSelected = Boolean(item.url && pathname && (pathname === item.url || pathname.startsWith(item.url + '/')));
  const Icon = item.icon!;
  const itemIcon = item.icon ? <Icon variant="Bulk"  size={drawerOpen ? 20 : 22} sx={{color:"text.main",'&:hover': {
                color: 'text.main'
              }}} /> : false;

  // active menu item on page load - MOVED BEFORE EARLY RETURN
  useEffect(() => {
    if (pathname && pathname.includes('product-details')) {
      if (item.url && item.url.includes('product-details')) {
        dispatch(activeItem({ openItem: [item.id] }));
      }
    }

    if (pathname && pathname.includes('kanban')) {
      if (item.url && item.url.includes('kanban')) {
        dispatch(activeItem({ openItem: [item.id] }));
      }
    }

    if (pathname === item.url) {
      dispatch(activeItem({ openItem: [item.id] }));
    }
    // eslint-disable-next-line
  }, [pathname]);

  // Don't render if user doesn't have permission
  if (!hasAccess) {
    return null;
  }


  const textColor = theme.palette.mode === ThemeMode.DARK ? 'text.main' : 'text.main';
  const iconSelectedColor = 'text.main';
  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <ListItemButton
          component={Link}
          to={item.url!}
          target={itemTarget}
          disabled={item.disabled}
          selected={Boolean(isSelected)}
          sx={{
            zIndex: 1201,
            // pl: drawerOpen ? `${level * 20}px` : 1.5,
            // py: !drawerOpen && level === 1 ? 1.25 : 1,
            ...(drawerOpen && {
              '&:hover': {
                bgcolor: 'secondary.main'
              },
              '&.Mui-selected': {
                bgcolor: 'secondary.dark',
                '&:hover': {
                bgcolor: 'secondary.dark'
              },
              }
            }),
            ...(drawerOpen &&
              level === 1 && {
                mx: 1.25,
                // my: 0.5,
                borderRadius: 1,
                '&:hover': {
                  bgcolor: 'secondary.main',
                  // color: 'text.main',
           
                },
                '&.Mui-selected': {
                  bgcolor: 'secondary.dark',
                  color: 'text.main',
                  '&:hover': {
                bgcolor: 'secondary.dark'
              },
                  // '& .MuiListItemIcon-root': {
                  //   color: 'text.main'
                  // },
               
                }
              }),
            ...(!drawerOpen && {
              // px: 2.75,
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'secondary.main'
              },
              '&.Mui-selected': {
            '&:hover': {
                bgcolor: 'secondary.dark'
              },
                // '&:hover': {
                //   bgcolor: 'secondary.dark'
                // },
                bgcolor: 'secondary.dark'
              }
            })
          }}
          {...(downLG && {
            onClick: () => dispatch(openDrawer(false))
          })}
        >
          {itemIcon && (
            <ListItemIcon
              sx={{
                minWidth: 38,
                color: isSelected ? iconSelectedColor : textColor,
                ...(!drawerOpen &&
                  level === 1 && {
                    borderRadius: 1,
                    // paddingX:3,

                    alignItems: 'center',
                    justifyContent: 'center',
                    // '&:hover': {
                    //   bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.main' : 'secondary.main'
                    // }
                  }),
                // ...(!drawerOpen &&
                //   isSelected && {
                //     bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.dark' : 'primary.main',
                    
                //   })
              }}
            >
              <Box>{itemIcon}</Box>

            </ListItemIcon>
          )}

          {!itemIcon && drawerOpen && (
            <ListItemIcon
              sx={{
                minWidth: 30
              }}
            >
              <Dot size={isSelected ? 6 : 5} color={isSelected ? 'primary' : 'secondary'} />
            </ListItemIcon>
          )}

          {drawerOpen && (
            <ListItemText
              primary={
                <NavItemTitle
                  title={String(item.title ?? '')}
                  fullTitle={item.titleTooltip}
                  drawerOpen={drawerOpen}
                  sx={{ color: isSelected ? iconSelectedColor : textColor, fontWeight: isSelected ? 500 : 400 }}
                />
              }
            />
          )}
          {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            />
          )}
        </ListItemButton>
      ) : (
        <ListItemButton
          component={Link}
          to={item.url!}
          target={itemTarget}
          disabled={item.disabled}
          selected={Boolean(isSelected)}
          sx={{
            zIndex: 1201,
            px: 0.5,
            py: 0.1,
            ...(drawerOpen && {
              '&:hover': {
                bgcolor: 'seconday.main'
              },
              '&.Mui-selected': {
                bgcolor: 'secondary.dark',
                color: iconSelectedColor,
                
              }
            }),
            ...(!drawerOpen && {
              '&:hover': {
                bgcolor: 'secondary.main'
              },
              '&.Mui-selected': {
                // '&:hover': {
                //   bgcolor: 'secondary.dark'
                // },
                bgcolor: 'secondary.dark'
              }
            })
          }}
        >
          {itemIcon && (
            <ListItemIcon
              sx={{
                ...(!drawerOpen && {
                  borderRadius: 1,

                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    bgcolor: 'secondary.main'
                  }
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: 'secondary.dark',
                    // '&:hover': {
                    //   bgcolor: 'secondary.main'
                    // }
                  })
              }}
            >
              <Box>{itemIcon}</Box>
            </ListItemIcon>
          )}

          <ListItemText
            primary={
              <NavItemTitle
                title={String(item.title ?? '')}
                fullTitle={item.titleTooltip}
                drawerOpen={drawerOpen}
                sx={{ color: isSelected ? iconSelectedColor : textColor, fontWeight: isSelected ? 500 : 400, mb: 0.5 }}
              />
            }
          />
          {(drawerOpen || !drawerOpen) && item.chip && (
            <Chip
              color={item.chip.color}
              variant={item.chip.variant}
              size={item.chip.size}
              label={item.chip.label}
              avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
              sx={{ ml: 1 }}
            />
          )}
        </ListItemButton>
      )}
    </>
  );
};

export default NavItem;
